<template>
  <div class="pgn-shell">
    <div class="pgn-info">
      Showing <strong>{{ startItem }}</strong> – <strong>{{ endItem }}</strong> of <strong>{{ total }}</strong> {{ itemsLabel }}
    </div>

    <div class="pgn-controls">
      <div v-if="showPerPageSelector" class="pgn-perpage">
        <span class="pgn-perpage__label">Per page</span>
        <div class="pgn-perpage__select-wrap">
          <select
            :value="perPage"
            @change="$emit('per-page-change', parseInt($event.target.value))"
            class="pgn-perpage__select"
          >
            <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
          <vue-feather type="chevron-down" size="12" class="pgn-perpage__caret" />
        </div>
      </div>

      <nav class="pgn-nav">
        <button
          v-if="showFirstLast"
          type="button"
          class="pgn-btn"
          :disabled="currentPage === 1"
          @click="$emit('page-change', 1)"
          title="First page"
        >
          <vue-feather type="chevrons-left" size="14" />
        </button>

        <button
          type="button"
          class="pgn-btn"
          :disabled="!hasPrev"
          @click="$emit('page-change', currentPage - 1)"
          title="Previous page"
        >
          <vue-feather type="chevron-left" size="14" />
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          type="button"
          class="pgn-btn pgn-btn--page"
          :class="{
            'pgn-btn--active': page === currentPage,
            'pgn-btn--ellipsis': page === '...',
          }"
          :disabled="page === '...'"
          @click="page !== '...' && $emit('page-change', page)"
        >
          {{ page }}
        </button>

        <button
          type="button"
          class="pgn-btn"
          :disabled="!hasNext"
          @click="$emit('page-change', currentPage + 1)"
          title="Next page"
        >
          <vue-feather type="chevron-right" size="14" />
        </button>

        <button
          v-if="showFirstLast"
          type="button"
          class="pgn-btn"
          :disabled="currentPage === totalPages"
          @click="$emit('page-change', totalPages)"
          title="Last page"
        >
          <vue-feather type="chevrons-right" size="14" />
        </button>
      </nav>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "PaginationControls",
  props: {
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },
    total: { type: Number, required: true },
    perPage: { type: Number, default: 10 },
    hasNext: { type: Boolean, default: false },
    hasPrev: { type: Boolean, default: false },
    itemsLabel: { type: String, default: "items" },
    showPerPageSelector: { type: Boolean, default: true },
    perPageOptions: { type: Array, default: () => [10, 25, 50, 100] },
    showFirstLast: { type: Boolean, default: true },
    maxVisiblePages: { type: Number, default: 5 },
  },
  emits: ["page-change", "per-page-change"],
  setup(props) {
    const startItem = computed(() => props.total === 0 ? 0 : (props.currentPage - 1) * props.perPage + 1);
    const endItem = computed(() => Math.min(props.currentPage * props.perPage, props.total));

    const visiblePages = computed(() => {
      const pages = [];
      const total = props.totalPages;
      const current = props.currentPage;
      const max = props.maxVisiblePages;

      if (total <= max) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        const halfMax = Math.floor(max / 2);
        let startPage = Math.max(1, current - halfMax);
        let endPage = Math.min(total, current + halfMax);

        if (current <= halfMax) endPage = max;
        else if (current >= total - halfMax) startPage = total - max + 1;

        if (startPage > 1) { pages.push(1); if (startPage > 2) pages.push("..."); }
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        if (endPage < total) { if (endPage < total - 1) pages.push("..."); pages.push(total); }
      }

      return pages;
    });

    return { startItem, endItem, visiblePages };
  },
};
</script>

<style scoped>
.pgn-shell {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface) 90%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  margin-top: 0.5rem;
  padding: 0.65rem 1rem;
}

.pgn-info {
  color: var(--hs-muted);
  font-size: 0.8rem;
}

.pgn-info strong {
  color: var(--hs-text);
  font-weight: 750;
}

.pgn-controls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.pgn-perpage {
  align-items: center;
  display: flex;
  gap: 0.45rem;
}

.pgn-perpage__label {
  color: var(--hs-muted);
  font-size: 0.78rem;
}

.pgn-perpage__select-wrap {
  align-items: center;
  background: color-mix(in srgb, var(--hs-bg-soft) 80%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 8px;
  display: flex;
  gap: 0.25rem;
  padding: 0.28rem 0.55rem;
  position: relative;
}

.pgn-perpage__select {
  appearance: none;
  background: transparent;
  border: none;
  color: var(--hs-text);
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  outline: none;
  padding-right: 0.15rem;
}

.pgn-perpage__caret {
  color: var(--hs-muted);
  flex-shrink: 0;
  pointer-events: none;
}

.pgn-nav {
  align-items: center;
  display: flex;
  gap: 0.25rem;
}

.pgn-btn {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--hs-muted);
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 600;
  height: 2rem;
  justify-content: center;
  min-width: 2rem;
  padding: 0 0.35rem;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.pgn-btn:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.pgn-btn:not(:disabled):hover {
  background: color-mix(in srgb, var(--hs-primary) 8%, var(--hs-surface));
  border-color: color-mix(in srgb, var(--hs-primary) 22%, transparent);
  color: var(--hs-primary);
}

.pgn-btn--active {
  background: var(--hs-primary) !important;
  border-color: var(--hs-primary) !important;
  color: #fff !important;
}

.pgn-btn--ellipsis {
  cursor: default;
  pointer-events: none;
}
</style>
